import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { resolveApiErrorMessage } from '@formosa/shared/i18n/resolveApiErrorMessage';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { creatorApi } from '../../api/creatorApi';
import { publicJourneyApi } from '../../api/publicJourneyApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import SafeImage from '../../components/SafeImage';
import ContentReportDialog from '../social-post/ContentReportDialog';
import TargetComments from '../comments/TargetComments';
import { journeyCommentApi } from '../../api/journeyCommentApi';
import { normalizeCreatorProfile } from '../creator/creatorProfileModel';
import {
  createJourneyCopyClientRequestId,
  normalizeCopyJourneyResult,
  normalizeJourneyEngagement,
  normalizePublicJourney,
  normalizeTimelinePage,
  resolvePublicJourneyView,
} from './publicJourneyModel';
import { publicTimelineTypeLabelKey } from './publicJourneyPresentation';
import { formatPlainDate, formatPlainDateRange } from '../../i18n/travelFormat';
import PublicJourneyStoryView from './PublicJourneyStoryView';
import { canOfferJourneyImShare } from './journeyImShareModel';
import ShareJourneyToImSheet from './ShareJourneyToImSheet';
import PublicJourneyEngagementBar from './PublicJourneyEngagementBar';
import PlanningMapSurface from '../journey-planning/PlanningMapSurface';
import '../journey-planning/journey-planning.css';
import './public-journey.css';

const formatTime = (value) => {
  if (!value) return '';
  if (Array.isArray(value) && value.length >= 5) {
    return `${String(value[3]).padStart(2, '0')}:${String(value[4]).padStart(2, '0')}`;
  }
  return String(value).slice(0, 5);
};

export default function PublicJourneyPage() {
  const { publicationId } = useParams();
  const id = publicationId;
  const { t, tf, tp, intlLocale } = useTravelTranslation();
  const formatDate = (value) => formatPlainDate(value, intlLocale);
  const { status, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  /** 來自 Saved Journeys「複製並開始規劃」導引；僅聚焦既有 Copy CTA，不自動送出。 */
  const copyIntent = searchParams.get('intent') === 'copy';
  /** 模式只由 URL 決定；切換不重新載入詳情／互動狀態，也不影響留言。 */
  const view = resolvePublicJourneyView(searchParams.get('view'));
  const requestId = useRef(0);
  /** 同一次 copy intent 共用；成功後才清空，以便下次明確操作產生新 key。 */
  const copyClientRequestIdRef = useRef(null);
  /** 按讚／收藏單一進行中保護：載入狀態與送出共用，連點不會重複送出。 */
  const engagementInFlightRef = useRef(false);
  const copyCtaRef = useRef(null);
  const commentsRef = useRef(null);
  const commentsIntentRef = useRef(false);
  const focusComments = useCallback(() => {
    const section = commentsRef.current;
    if (!section) return;
    commentsIntentRef.current = true;
    const target = section.querySelector('textarea:not(:disabled), .comments__signin') || section;
    target.focus({ preventScroll: true });
    target.scrollIntoView?.({ block: 'center', behavior: 'instant' });
  }, []);
  const [journey, setJourney] = useState(null);
  const [creator, setCreator] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [timeline, setTimeline] = useState({ items: [], hasMore: false, nextCursor: null });
  const [timelinePhase, setTimelinePhase] = useState('loading');
  const [timelineError, setTimelineError] = useState('');
  const [engagement, setEngagement] = useState(null);
  const [busy, setBusy] = useState('');
  const [actionError, setActionError] = useState('');
  const [copyNotice, setCopyNotice] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState('');

  async function loadTimeline(publicId, cursor = null, append = false, token = requestId.current) {
    setTimelinePhase(append ? 'more' : 'loading'); setTimelineError('');
    try {
      const page = normalizeTimelinePage(await publicJourneyApi.getTimeline(publicId, { ...(cursor || {}), limit: 30 }));
      if (token !== requestId.current) return;
      setTimeline((current) => ({ ...page, items: append ? [...current.items, ...page.items] : page.items }));
      setTimelinePhase('ready');
    } catch (cause) {
      if (token !== requestId.current) return;
      setTimelineError(resolveSafeUserVisibleError(cause, t, 'travel.publicJourney.itineraryLoadFailed')); setTimelinePhase('error');
    }
  }

  async function load() {
    const token = ++requestId.current;
    if (!/^[1-9]\d*$/.test(id || '')) { setPhase('not-found'); return; }
    setPhase('loading'); setError(''); setJourney(null); setCreator(null); setEngagement(null);
    setTimeline({ items: [], hasMore: false, nextCursor: null }); setTimelinePhase('loading');
    try {
      const detail = normalizePublicJourney(await publicJourneyApi.getDetail(id));
      if (token !== requestId.current) return;
      setJourney(detail); setPhase('ready');
      loadTimeline(id, null, false, token);
      creatorApi.getProfile(detail.creatorMemberId).then((value) => {
        if (token === requestId.current) setCreator(normalizeCreatorProfile(value));
      }).catch(() => {});
      if (isAuthenticated) publicJourneyApi.getEngagement(id).then((value) => {
        if (token === requestId.current) setEngagement(normalizeJourneyEngagement(value));
      }).catch(() => {});
    } catch (cause) {
      if (token !== requestId.current) return;
      const notFound = cause?.raw?.key === 'travel.publication.projection.not.available'
        || cause?.raw?.code === 95200 || cause?.status === 404;
      if (notFound) setPhase('not-found'); else { setError(resolveSafeUserVisibleError(cause, t, 'travel.publicJourney.itineraryLoadFailed')); setPhase('error'); }
    }
  }

  useEffect(() => { if (status !== 'unknown') load(); return () => { requestId.current += 1; }; }, [id, status, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!copyIntent || phase !== 'ready' || !journey) return;
    if (!journey.allowCopy || !copyCtaRef.current) return;
    const node = copyCtaRef.current;
    try {
      node.focus({ preventScroll: true });
    } catch {
      node.focus();
    }
    if (typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [copyIntent, phase, journey]);

  useEffect(() => {
    if (phase === 'ready' && location.hash === '#journey-comments') focusComments();
  }, [phase, id, location.hash, focusComments]);

  useEffect(() => {
    if (phase !== 'ready' || typeof ResizeObserver === 'undefined') return undefined;
    const section = commentsRef.current;
    if (!section?.parentElement) return undefined;
    // Async itinerary/media may grow above comments after the initial jump.
    // Only preserve visibility while the requested comment control still owns focus.
    const observer = new ResizeObserver(() => {
      const target = document.activeElement;
      if (commentsIntentRef.current && section.contains(target)) {
        target.scrollIntoView?.({ block: 'center', behavior: 'instant' });
      }
    });
    observer.observe(section.parentElement);
    return () => observer.disconnect();
  }, [phase, id]);

  /** 以 push 寫入歷史以支援上一頁／下一頁；保留其他 query 與 hash。overview 不帶參數，維持舊網址。 */
  function switchView(next) {
    if (next === view) return;
    const params = new URLSearchParams(location.search);
    if (next === 'story') params.set('view', 'story'); else params.delete('view');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash });
  }

  function requireAuth() {
    if (isAuthenticated) return true;
    navigate('/login', { state: { from: authReturnLocation(location) } }); return false;
  }

  async function toggle(kind) {
    if (!requireAuth() || busy || engagementInFlightRef.current) return;
    engagementInFlightRef.current = true;
    setBusy(kind); setActionError('');
    try {
      let state = engagement;
      if (!state) {
        // 狀態未知時按鈕顯示為「未按讚／未收藏」：先載入一次，再依畫面意圖執行；已是目標狀態就不送出，絕不從未知狀態取消。
        try { state = normalizeJourneyEngagement(await publicJourneyApi.getEngagement(id)); }
        catch { setActionError(t('travel.publicJourney.engagementLoadFailed')); return; }
        setEngagement(state);
        if (state[kind]) return;
      }
      const active = state[kind];
      const api = kind === 'liked' ? (active ? publicJourneyApi.unlike : publicJourneyApi.like) : (active ? publicJourneyApi.unsave : publicJourneyApi.save);
      try {
        const result = await api(id);
        if (result?.changed) setEngagement((current) => ({ ...current, [kind]: !active,
          [kind === 'liked' ? 'likeCount' : 'saveCount']: Math.max(0, current[kind === 'liked' ? 'likeCount' : 'saveCount'] + (active ? -1 : 1)) }));
      } catch {
        setActionError(kind === 'liked' ? t('travel.publicJourney.likeFailed') : t('travel.publicJourney.saveFailed'));
      }
    } finally {
      engagementInFlightRef.current = false;
      setBusy('');
    }
  }

  async function copyJourney() {
    if (!requireAuth() || busy) return;
    if (!copyClientRequestIdRef.current) {
      copyClientRequestIdRef.current = createJourneyCopyClientRequestId();
    }
    const clientRequestId = copyClientRequestIdRef.current;
    setBusy('copy');
    setActionError('');
    setCopyNotice('');
    try {
      const result = normalizeCopyJourneyResult(
        await publicJourneyApi.copy(id, { clientRequestId }),
      );
      if (!result.journeyId) {
        setActionError(t('travel.publicJourney.copyFailed'));
        return;
      }
      copyClientRequestIdRef.current = null;
      const successMessage = t('travel.publicJourney.copySuccess');
      setCopyNotice(successMessage);
      navigate(`/journeys/${result.journeyId}/planning`, {
        state: { notice: successMessage },
      });
    } catch (cause) {
      setActionError(resolveApiErrorMessage(cause, t, 'travel.publicJourney.copyFailed'));
    } finally {
      setBusy((current) => (current === 'copy' ? '' : current));
    }
  }

  const viewerMemberId = user?.memberId ?? user?.id;
  const ownJourney = isAuthenticated && journey && String(viewerMemberId) === String(journey.creatorMemberId);
  const showCopyCta = Boolean(journey?.allowCopy);
  const showImShare = canOfferJourneyImShare({ isAuthenticated, journey });
  const copyBusy = busy === 'copy';
  const engagementMoreItems = [];
  if (showImShare) {
    engagementMoreItems.push({
      id: 'share-im',
      label: t('travel.publicJourney.more.shareIm'),
      testId: 'public-journey-share-im',
      onSelect: () => setShareOpen(true),
    });
  }
  if (ownJourney) {
    engagementMoreItems.push({
      id: 'group-trip',
      label: t('travel.publicJourney.publicGroupTrips'),
      testId: 'public-journey-group-trip',
      onSelect: () => navigate(`/group-trips/new?publicationId=${id}`),
    });
  } else {
    engagementMoreItems.push({
      id: 'group-trip',
      label: t('travel.publicJourney.publicGroupTrips'),
      testId: 'public-journey-group-trip',
      onSelect: () => navigate('/public/group-trips'),
    });
  }
  if (isAuthenticated && !ownJourney) {
    engagementMoreItems.push({
      id: 'report',
      label: t('travel.publicJourney.more.report'),
      testId: 'public-journey-report',
      onSelect: () => {
        if (!requireAuth()) return;
        setReportOpen(true);
      },
    });
  }

  if (status === 'unknown' || phase === 'loading') {
    return (
      <main className="journey-state" aria-live="polite">
        <div className="explore-spinner" />
        <p>{t('travel.publicJourney.loading')}</p>
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-state">
        <h1>{t('travel.publicJourney.unavailable')}</h1>
        <p>{t('travel.publicJourney.unavailableHint')}</p>
        <Link to="/explore">{t('travel.publicJourney.backToExplore')}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="journey-state" role="alert">
        <h1>{t('travel.publicJourney.loadFailedTitle')}</h1>
        <p>{error}</p>
        <button type="button" onClick={load}>{t('travel.publicJourney.tryAgain')}</button>
      </main>
    );
  }

  const hasCover = Boolean(journey.coverUrl);

  return <main className="public-journey">
    <article className={hasCover ? 'journey-hero' : 'journey-hero journey-hero--no-cover'}>
      {hasCover ? (
        <SafeImage className="journey-hero__cover" src={journey.coverUrl} alt={tf('travel.publicJourney.coverAlt', { title: journey.title })} />
      ) : null}
      <div className="journey-hero__content">
        <p className="journey-eyebrow">{t('travel.publicJourney.publicItinerary')}</p><h1>{journey.title}</h1>
        {journey.summary ? <p className="journey-hero__summary">{journey.summary}</p> : null}
        <div className="journey-facts">
          {journey.destination ? <span>⌖ {journey.destination}</span> : null}
          {journey.startDate && journey.endDate ? <span>{formatPlainDateRange(journey.startDate, journey.endDate, intlLocale)}</span> : null}
          <span>{tp('travel.publicJourney.dayCount', journey.dayCount)}</span>
          <span>{tp('travel.publicJourney.activityCount', journey.activityCount)}</span>
        </div>
        {/* 完整使用說明預設收合（原生 details：鍵盤可操作、展開狀態由瀏覽器提供） */}
        <details className="journey-action-guide" data-testid="public-journey-action-guide">
          <summary
            className="journey-action-guide__title"
            data-expand-label={t('travel.publicJourney.actionGuide.expand')}
            data-collapse-label={t('travel.publicJourney.actionGuide.collapse')}
          >{t('travel.publicJourney.actionGuideTitle')}</summary>
          <p>{t('travel.publicJourney.actionGuideSave')}</p>
          <p>{t('travel.publicJourney.actionGuideCopy')}</p>
        </details>
        {copyIntent && showCopyCta ? (
          <p className="journey-copy-intent" data-testid="public-journey-copy-intent" role="status">
            {t('travel.publicJourney.copyIntentFocus')}
          </p>
        ) : null}
        {copyIntent && !showCopyCta ? (
          <p className="journey-inline-error" data-testid="public-journey-copy-unavailable" role="status">
            {t('travel.publicJourney.copyUnavailable')}
          </p>
        ) : null}
        {showCopyCta ? (
          <div className={copyIntent ? 'journey-copy-cta journey-copy-cta--intent' : 'journey-copy-cta'}>
            <button
              ref={copyCtaRef}
              type="button"
              className="journey-copy-cta__button"
              data-testid="public-journey-copy"
              aria-busy={copyBusy}
              disabled={Boolean(busy)}
              onClick={copyJourney}
            >
              {copyBusy ? t('travel.publicJourney.copyBusy') : t('travel.publicJourney.copyCta')}
            </button>
            <p className="journey-copy-cta__hint">{t('travel.publicJourney.copyHint')}</p>
          </div>
        ) : null}
        <div className="journey-hero__footer">
          <Link className="journey-creator" to={`/social/creators/${journey.creatorMemberId}`}>
            <SafeImage className="journey-creator__avatar" src={creator?.avatarUrl} alt="" />
            <span>
              <small>{t('travel.publicJourney.sharedBy')}</small>
              <strong>{creator?.displayName || t('travel.publicJourney.travelerFallback')}</strong>
            </span>
          </Link>
        </div>
        <PublicJourneyEngagementBar
          engagement={engagement}
          busy={Boolean(busy)}
          onToggleLiked={() => toggle('liked')}
          onToggleSaved={() => toggle('saved')}
          onOpenComments={focusComments}
          moreItems={engagementMoreItems}
        />
        {copyNotice ? <p className="journey-inline-success" role="status">{copyNotice}</p> : null}
        {actionError ? <p className="journey-inline-error" role="alert">{actionError}</p> : null}
      </div>
    </article>

    <div className="journey-view-switch" role="group" aria-label={t('travel.publicJourney.view.label')} data-testid="public-journey-view-switch">
      <button
        type="button"
        aria-pressed={view === 'overview'}
        data-testid="public-journey-view-overview"
        onClick={() => switchView('overview')}
      >
        {t('travel.publicJourney.view.overview')}
      </button>
      <button
        type="button"
        aria-pressed={view === 'story'}
        data-testid="public-journey-view-story"
        onClick={() => switchView('story')}
      >
        {t('travel.publicJourney.view.story')}
      </button>
    </div>

    {/* 單一 slot：切換模式時只替換此區，下方共用留言不重新掛載 */}
    {view === 'story' ? (
      <PublicJourneyStoryView
        publicationId={journey.publicationId}
        story={journey.story}
        viewerKey={`${isAuthenticated ? '1' : '0'}:${user?.id || ''}`}
        onRequireAuth={requireAuth}
        onShowOverview={() => switchView('overview')}
      />
    ) : <>
    {journey.itineraryUnavailable ? (
      <section className="journey-itinerary" aria-labelledby="journey-itinerary-title">
        <div className="journey-section-heading">
          <div>
            <p className="journey-eyebrow">{t('travel.publicJourney.publishedRoute')}</p>
            <h2 id="journey-itinerary-title">{t('travel.publicJourney.itinerary')}</h2>
          </div>
        </div>
        <div className="timeline-state">
          <h3>{t('travel.publicJourney.itineraryUnavailableTitle')}</h3>
          <p>{t('travel.publicJourney.itineraryUnavailableHint')}</p>
        </div>
      </section>
    ) : journey.itinerary?.days?.length || journey.itinerary?.unscheduledActivities?.length ? (
      <section className="journey-itinerary" aria-labelledby="journey-itinerary-title">
        <div className="journey-section-heading">
          <div>
            <p className="journey-eyebrow">{t('travel.publicJourney.publishedRoute')}</p>
            <h2 id="journey-itinerary-title">{t('travel.publicJourney.itinerary')}</h2>
          </div>
          <span>{tp('travel.publicJourney.mappedStops', journey.itinerary.mapSummary.markerCount)}</span>
        </div>
        <PlanningMapSurface
          markers={journey.itinerary.markers}
          selectedKey={selectedMarker}
          onSelectMarker={setSelectedMarker}
        />
        <ol className="itinerary-days">
          {journey.itinerary.days.map((day) => (
            <li key={day.dayId || `day-${day.dayNumber}`}>
              <h4>
                {tf('travel.publicJourney.dayHeading', { number: day.dayNumber })}
                {day.title ? ` · ${day.title}` : ''}
                {day.date ? ` · ${formatDate(day.date)}` : ''}
              </h4>
              <ol>
                {day.activities.map((activity) => (
                  <li key={activity.activityId}>
                    <button
                      type="button"
                      className={selectedMarker === activity.activityId ? 'is-selected' : undefined}
                      aria-label={`${activity.title} · ${tf('travel.publicJourney.dayHeading', { number: day.dayNumber })}`}
                      onClick={() => activity.hasCoordinates && setSelectedMarker(activity.activityId)}
                    >
                      <strong>{activity.title}</strong>
                      <span>
                        {formatTime(activity.plannedStartTime)}
                        {activity.locationLabel ? ` · ${activity.locationLabel}` : ''}
                        {activity.hasCoordinates ? '' : ` · ${t('travel.publicJourney.noMapPin')}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </li>
          ))}
        </ol>
        {journey.itinerary.unscheduledActivities.length ? (
          <div className="itinerary-unscheduled">
            <h4>{t('travel.publicJourney.unscheduled')}</h4>
            <ol>
              {journey.itinerary.unscheduledActivities.map((activity) => (
                <li key={activity.activityId}>
                  <button
                    type="button"
                    className={selectedMarker === activity.activityId ? 'is-selected' : undefined}
                    onClick={() => activity.hasCoordinates && setSelectedMarker(activity.activityId)}
                  >
                    <strong>{activity.title}</strong>
                    <span>{activity.locationLabel || t('travel.publicJourney.noMappedLocation')}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </section>
    ) : null}

    <section className="journey-timeline" aria-labelledby="journey-timeline-title">
      <div className="journey-section-heading">
        <div>
          <p className="journey-eyebrow">{t('travel.publicJourney.timelineEyebrow')}</p>
          <h2 id="journey-timeline-title">{t('travel.publicJourney.timelineTitle')}</h2>
        </div>
        <span>{tp('travel.publicJourney.sharedActivities', journey.activityCount)}</span>
      </div>
      {timelinePhase === 'loading' ? (
        <div className="timeline-state" aria-live="polite">
          <div className="explore-spinner" />
          <p>{t('travel.publicJourney.timelineLoading')}</p>
        </div>
      ) : null}
      {timelinePhase === 'error' && timeline.items.length === 0 ? (
        <div className="timeline-state" role="alert">
          <h3>{t('travel.publicJourney.timelineUnavailable')}</h3>
          <p>{timelineError}</p>
          <button type="button" onClick={() => loadTimeline(id)}>{t('travel.publicJourney.timelineRetry')}</button>
        </div>
      ) : null}
      {timelinePhase === 'ready' && timeline.items.length === 0 ? (
        <div className="timeline-state">
          <h3>{t('travel.publicJourney.timelineEmptyTitle')}</h3>
          <p>{t('travel.publicJourney.timelineEmptyHint')}</p>
        </div>
      ) : null}
      {timeline.items.length ? (
        <ol className="timeline-list">
          {timeline.items.map((item) => (
            <li key={item.timelineItemId} className={`timeline-card timeline-card--${String(item.type || '').toLowerCase()}`}>
              <div className="timeline-card__marker" aria-hidden="true" />
              <article>
                <div className="timeline-card__meta">
                  <span>
                    {item.dayIndex == null
                      ? t('travel.publicJourney.journeyMemory')
                      : tf('travel.publicJourney.dayHeading', { number: item.dayIndex })}
                  </span>
                  <span>{t(publicTimelineTypeLabelKey(item))}</span>
                </div>
                <h3>{item.title}</h3>
                {item.content ? <p>{item.content}</p> : null}
                <div className="timeline-card__facts">
                  {item.plannedStartTime ? (
                    <time>
                      {formatTime(item.plannedStartTime)}
                      {item.plannedEndTime ? ` – ${formatTime(item.plannedEndTime)}` : ''}
                    </time>
                  ) : null}
                  {item.location ? <span>⌖ {item.location}</span> : null}
                </div>
              </article>
            </li>
          ))}
        </ol>
      ) : null}
      {timeline.hasMore && timeline.nextCursor ? (
        <button
          className="timeline-more"
          type="button"
          disabled={timelinePhase === 'more'}
          onClick={() => loadTimeline(id, timeline.nextCursor, true)}
        >
          {timelinePhase === 'more' ? t('travel.publicJourney.loadingMore') : t('travel.publicJourney.loadMore')}
        </button>
      ) : null}
      {timelineError && timeline.items.length ? <p className="journey-inline-error" role="alert">{timelineError}</p> : null}
    </section>
    </>}

    <section ref={commentsRef} id="journey-comments" className="journey-comments" tabIndex={-1} aria-label={t('travel.publicJourney.comments.title')}>
      <TargetComments
        targetId={id}
        commentApi={journeyCommentApi}
        title={t('travel.publicJourney.comments.title')}
        composeLabel={t('travel.publicJourney.comments.compose')}
        emptyHint={t('travel.publicJourney.comments.empty')}
      />
    </section>

    {reportOpen ? (
      <ContentReportDialog
        open
        targetType="JOURNEY_PUBLICATION"
        targetId={id}
        titleKey="travel.safety.reportContent"
        onClose={() => setReportOpen(false)}
      />
    ) : null}

    {shareOpen && showImShare ? (
      <ShareJourneyToImSheet
        publicationId={id}
        journeyTitle={journey.title}
        onClose={() => setShareOpen(false)}
      />
    ) : null}
  </main>;
}
